import { useEffect, useMemo, useState } from "react"
import {
  Circle,
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet"
import { latLngBounds, LatLngBounds } from "leaflet"

import { DeliveryFeeSettings } from "../../../../models/DeliveryFeeSettings"
import { calculateDeliveryFee, getFeeBands } from "../../../../lib/deliveryFee"
import { distanceMeters, pointAtDistance, Point } from "../../../../lib/geo"
import { TILE_ATTRIBUTION, TILE_URL } from "../../../../lib/mapTiles"

import "leaflet/dist/leaflet.css"

export type DeliveryFeeMapProps = {
  deliveryFeeSettings: DeliveryFeeSettings
  hasUnsavedChanges: boolean
}

const MILES_TO_METERS = 1609.344
const DEFAULT_FIT_RADIUS_MILES = 2.5

export const DeliveryFeeMap: React.FC<DeliveryFeeMapProps> = ({ deliveryFeeSettings, hasUnsavedChanges }) => {
  const shopLocation = deliveryFeeSettings.shopLocation
  const { maxMiles } = deliveryFeeSettings
  const bands = getFeeBands(deliveryFeeSettings)
  const [probe, setProbe] = useState<Point | null>(null)

  const outerBounds = useMemo<LatLngBounds | null>(() => {
    if (shopLocation == null || !Number.isFinite(maxMiles) || maxMiles <= 0) {
      return null
    }

    const fitRadiusMeters = Math.min(maxMiles, DEFAULT_FIT_RADIUS_MILES) * MILES_TO_METERS
    const northEast = pointAtDistance(shopLocation, fitRadiusMeters, 45)
    const southWest = pointAtDistance(shopLocation, fitRadiusMeters, 225)

    return latLngBounds(
      { lat: southWest.latitude, lng: southWest.longitude },
      { lat: northEast.latitude, lng: northEast.longitude }
    )
  }, [shopLocation, maxMiles])

  if (shopLocation == null) {
    return (
      <div className="mt-8 border rounded-md px-4 py-3 text-sm text-gray-500">
        The shop location is not set, so the delivery range map is not available.
      </div>
    )
  }

  if (outerBounds == null) {
    return (
      <div className="mt-8 border rounded-md px-4 py-3 text-sm text-gray-500">
        Enter a valid max distance to see the delivery range map.
      </div>
    )
  }

  const probeText = getProbeText(probe, shopLocation, deliveryFeeSettings)

  return (
    <div className="mt-8 border rounded-md">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="text-sm font-medium text-gray-700">Delivery range map</div>

        {hasUnsavedChanges ? (
          <span className="text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full px-2 py-0.5">
            Previewing unsaved changes
          </span>
        ) : null}
      </div>

      <div className="relative">
        <MapContainer
          center={{ lat: shopLocation.latitude, lng: shopLocation.longitude }}
          zoom={13}
          className="w-full h-96"
        >
          <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />

          {bands
            .map((band, index) => ({ band, index }))
            .reverse()
            .map(({ band, index }) => (
              <Circle
                key={band.endMiles}
                center={{ lat: shopLocation.latitude, lng: shopLocation.longitude }}
                radius={band.endMiles * MILES_TO_METERS}
                interactive={false}
                pathOptions={{
                  color: bandColor(index, bands.length),
                  weight: 2,
                  opacity: 0.9,
                  fill: false,
                }}
              />
            ))}

          <CircleMarker
            center={{ lat: shopLocation.latitude, lng: shopLocation.longitude }}
            radius={5}
            pathOptions={{
              color: "#ffffff",
              weight: 2,
              fillColor: "#1f2937",
              fillOpacity: 1,
            }}
          >
            <Tooltip direction="top">Shop</Tooltip>
          </CircleMarker>

          {probe != null ? (
            <CircleMarker
              center={{ lat: probe.latitude, lng: probe.longitude }}
              radius={4}
              pathOptions={{
                color: "#1f2937",
                weight: 1,
                fillColor: "#f59e0b",
                fillOpacity: 1,
              }}
            />
          ) : null}

          <MapInteraction onProbe={setProbe} />
          <FitBounds bounds={outerBounds} />
        </MapContainer>

        {probe != null ? (
          <div
            className="absolute top-2 right-2 bg-white border rounded-md px-3 py-1 text-sm font-medium shadow"
            style={{ zIndex: 1001 }}
          >
            {probeText}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const MapInteraction: React.FC<{ onProbe: (point: Point | null) => void }> = ({ onProbe }) => {
  useMapEvents({
    mousemove: e => onProbe({ latitude: e.latlng.lat, longitude: e.latlng.lng }),
    mouseout: () => onProbe(null),
    click: e => onProbe({ latitude: e.latlng.lat, longitude: e.latlng.lng }),
  })

  return null
}

const FitBounds: React.FC<{ bounds: LatLngBounds }> = ({ bounds }) => {
  const map = useMap()

  useEffect(() => {
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] })
    }
  }, [map, bounds])

  return null
}

function bandColor(index: number, total: number): string {
  const step = total === 1 ? 0 : index / (total - 1)
  const hue = 200 + 20 * step
  const lightness = 65 - 40 * step

  return `hsl(${hue}, 80%, ${lightness}%)`
}

function getProbeText(
  probe: Point | null,
  shopLocation: Point,
  deliveryFeeSettings: DeliveryFeeSettings
): string {
  if (probe == null) {
    return ""
  }

  const miles = distanceMeters(shopLocation, probe) / MILES_TO_METERS

  if (miles > deliveryFeeSettings.maxMiles) {
    return "Out of delivery range"
  }

  const fee = calculateDeliveryFee(miles, deliveryFeeSettings)

  return `${miles.toFixed(1)} mi · ${
    Number.isFinite(fee) ? `£${(fee / 100).toFixed(2)}` : "—"
  }`
}
